import { useState } from "react";
import api, { formatINR, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Pencil, Gift, Download } from "lucide-react";
import { toast } from "sonner";
import DataTable from "@/components/DataTable";

const empty = { name: "", phone: "", gender: "", spouse_or_parent_name: "", address: "", email: "", whatsapp_optin: true, birthday: "", credit_limit: 20000 };

const TIER_COLORS = {
  silver: "bg-slate-300 text-slate-800",
  gold: "bg-yellow-400 text-yellow-900",
  platinum: "bg-purple-300 text-purple-900",
};

export default function Customers() {
  const { user, activeBranchId, hasPerm } = useAuth();
  const isCashier = user.role === "cashier";
  const canCreate = !isCashier || hasPerm("customer_create_pos");
  const canEdit = user.role === "super_admin" || (user.role === "admin" && hasPerm("customer_edit"));
  const canAdjust = hasPerm("loyalty_adjust");

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [adjOpen, setAdjOpen] = useState(false);
  const [adj, setAdj] = useState({ customer_id: "", points_delta: 0, reason: "" });
  const [reloadKey, setReloadKey] = useState(0);
  const triggerReload = () => setReloadKey((k) => k + 1);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await api.put(`/customers/${editing}`, form); toast.success("Updated"); }
      else { await api.post("/customers", { ...form, branch_id: activeBranchId }); toast.success("Added"); }
      setOpen(false); setEditing(null); setForm(empty); triggerReload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; try { await api.delete(`/customers/${id}`); triggerReload(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const adjust = async (e) => {
    e.preventDefault();
    try { await api.post("/loyalty/adjust", adj); toast.success("Adjusted"); setAdjOpen(false); triggerReload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const startEdit = (c) => { setEditing(c.id); setForm({ ...empty, ...c }); setOpen(true); };

  const exportCsv = async () => {
    try {
      const res = await api.get("/export/customers", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "customers.csv"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Exported customers.csv (DOB excluded per policy)");
    } catch { toast.error("Export failed"); }
  };

  const spouseLabel = form.gender === "male" ? "Father's Name" : "Husband's Name";

  const columns = [
    { key: "name", label: "Name", sortable: true, render: (r) => <span className="font-medium">{r.name}</span> },
    { key: "phone", label: "Mobile", render: (r) => <span className="text-slate-600">{r.phone || "—"}</span> },
    { key: "gender", label: "Gender", render: (r) => <span className="capitalize text-slate-600">{r.gender || "—"}</span> },
    { key: "lifetime_spend", label: "Spend", sortable: true, className: "text-right", render: (r) => <span className="tabular-nums">{formatINR(r.lifetime_spend || 0)}</span> },
    { key: "loyalty_points", label: "Points", sortable: true, className: "text-right", render: (r) => <span className="font-semibold tabular-nums">{r.loyalty_points || 0}</span> },
    { key: "credit_limit", label: "Credit Limit", className: "text-right", render: (r) => <span className="tabular-nums">{formatINR(r.credit_limit || 0)}</span> },
    { key: "store_credit", label: "Store Credit", className: "text-right", render: (r) => <span className="tabular-nums text-blue-700">{formatINR(r.store_credit || 0)}</span> },
    { key: "tier", label: "Tier", render: (r) => <Badge className={`rounded-none uppercase ${TIER_COLORS[r.tier || "silver"]}`}>{r.tier || "silver"}</Badge> },
    { key: "imported_from_tally", label: "Source", render: (r) => r.imported_from_tally ? <span className="text-[10px] uppercase text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5">Tally</span> : <span className="text-[10px] text-slate-400">Live</span> },
  ];

  const filters = [
    { key: "gender", type: "select", label: "Gender", options: [{ value: "female", label: "Female" }, { value: "male", label: "Male" }] },
    { key: "tier", type: "select", label: "Tier", options: [{ value: "silver", label: "Silver" }, { value: "gold", label: "Gold" }, { value: "platinum", label: "Platinum" }] },
    { key: "imported", type: "select", label: "Source", options: [{ value: "true", label: "Imported (Tally)" }, { value: "false", label: "Live only" }] },
  ];

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Customers</h1>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-none" onClick={exportCsv} data-testid="export-customers-btn">
            <Download className="w-4 h-4 mr-2" />Export CSV
          </Button>
          {canCreate && (
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
              <DialogTrigger asChild>
                <Button className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="add-customer-btn"><Plus className="w-4 h-4 mr-2" />Add Customer</Button>
              </DialogTrigger>
              <DialogContent className="rounded-sm max-w-lg">
                <DialogHeader><DialogTitle>{editing ? "Edit Customer" : "New Customer"}</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>Name *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1" data-testid="customer-name" /></div>
                  <div><Label>Mobile</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="optional" className="rounded-none mt-1" data-testid="customer-phone" /></div>
                  <div><Label>Gender</Label>
                    <Select value={form.gender || "_none"} onValueChange={(v) => setForm({ ...form, gender: v === "_none" ? "" : v })}>
                      <SelectTrigger className="rounded-none mt-1" data-testid="customer-gender"><SelectValue placeholder="Not specified" /></SelectTrigger>
                      <SelectContent><SelectItem value="_none">Not specified</SelectItem><SelectItem value="female">Female</SelectItem><SelectItem value="male">Male</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Label>{spouseLabel} (optional)</Label><Input value={form.spouse_or_parent_name} onChange={(e) => setForm({ ...form, spouse_or_parent_name: e.target.value })} className="rounded-none mt-1" data-testid="customer-spouse" /></div>
                  <div className="col-span-2"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="optional" className="rounded-none mt-1" data-testid="customer-address" /></div>
                  <div className="col-span-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none mt-1" /></div>
                  <div className="col-span-2">
                    <Label>Date of Birth (for birthday campaigns — optional)</Label>
                    <Input type="date" value={form.birthday || ""} onChange={(e) => setForm({ ...form, birthday: e.target.value })} className="rounded-none mt-1" data-testid="customer-birthday" />
                  </div>
                  <div className="col-span-2">
                    <Label>Credit Limit (₹) — set to 0 to disable credit</Label>
                    <Input type="number" min="0" step="100" value={form.credit_limit || 0}
                      onChange={(e) => setForm({ ...form, credit_limit: parseFloat(e.target.value) || 0 })}
                      className="rounded-none mt-1" data-testid="customer-credit-limit" />
                  </div>
                  <div className="col-span-2 flex items-center justify-between border border-slate-200 p-2 text-sm">
                    <span>WhatsApp Opt-in</span>
                    <Switch checked={form.whatsapp_optin} onCheckedChange={(v) => setForm({ ...form, whatsapp_optin: v })} data-testid="customer-optin" />
                  </div>
                  <DialogFooter className="col-span-2"><Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="customer-save">{editing ? "Update" : "Create"}</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <DataTable
        endpoint="/customers"
        columns={columns}
        filters={filters}
        searchPlaceholder="Search by name, mobile, email..."
        extraQuery={{ branch_id: activeBranchId || undefined }}
        reloadKey={reloadKey}
        actions={(c) => (
          <div className="flex justify-end gap-1">
            {canAdjust && <Button size="sm" variant="ghost" className="rounded-none h-8 px-2" onClick={() => { setAdj({ customer_id: c.id, points_delta: 0, reason: "" }); setAdjOpen(true); }} data-testid={`adjust-${c.id}`}><Gift className="w-4 h-4" /></Button>}
            {canEdit && <Button size="sm" variant="ghost" className="rounded-none h-8 px-2" onClick={() => startEdit(c)} data-testid={`edit-customer-${c.id}`}><Pencil className="w-4 h-4" /></Button>}
            {canEdit && <Button size="sm" variant="ghost" className="rounded-none h-8 px-2 text-red-600" onClick={() => remove(c.id)} data-testid={`delete-customer-${c.id}`}><Trash2 className="w-4 h-4" /></Button>}
          </div>
        )}
        dataTestid="customers-table"
      />

      <Dialog open={adjOpen} onOpenChange={setAdjOpen}>
        <DialogContent className="rounded-sm max-w-sm">
          <DialogHeader><DialogTitle>Adjust Loyalty Points</DialogTitle></DialogHeader>
          <form onSubmit={adjust} className="space-y-3">
            <div><Label>Points (+/-)</Label><Input type="number" required value={adj.points_delta} onChange={(e) => setAdj({ ...adj, points_delta: parseInt(e.target.value) || 0 })} className="rounded-none mt-1" data-testid="loyalty-delta" /></div>
            <div><Label>Reason</Label><Input required value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })} className="rounded-none mt-1" /></div>
            <DialogFooter><Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-none">Apply</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
